import React from "react";
import { Card } from "../../../../shared";

/**
 * Append-only activity timeline for a Lead. Server emits events of
 * type "lead.created", "status.changed", "assignee.changed", and
 * "note.added"; this component renders each as a single line with a
 * timestamp.
 */
const ActivityLog = ({ activityLog }) => {
  const sorted = [...(activityLog || [])].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  return (
    <Card title="Activity">
      {sorted.length === 0 ? (
        <p className="text-sm text-gray-500">No activity yet.</p>
      ) : (
        <ol className="space-y-2">
          {sorted.map((entry, idx) => (
            <li
              key={entry._id || `${entry.type}-${entry.createdAt}-${idx}`}
              className="flex items-start gap-3 text-sm"
            >
              <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
              <div className="flex-1">
                <div className="text-gray-800">{describeEntry(entry)}</div>
                <div className="text-xs text-gray-500">
                  {formatDate(entry.createdAt)} · {formatActor(entry.actorId)}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
};

const describeEntry = (entry) => {
  const from = entry.fromValue;
  const to = entry.toValue;
  switch (entry.type) {
    case "lead.created":
      return `Lead created${to ? ` (${to})` : ""}`;
    case "status.changed":
      return `Status changed${from ? ` from ${from}` : ""}${
        to ? ` to ${to}` : ""
      }`;
    case "assignee.changed":
      if (!to) return "Lead unassigned";
      return `Assigned to ${formatActor(to)}`;
    case "note.added":
      return "Internal note added";
    case "customer.created":
      return `Customer tenant created${to ? ` (id: ${to})` : ""}`;
    default:
      return entry.type || "Activity";
  }
};

const formatActor = (actor) => {
  if (!actor) return "system";
  if (typeof actor === "string") return "Team member";
  const name = `${actor.firstName || ""} ${actor.lastName || ""}`.trim();
  return name || actor.email || "Team member";
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
};

export default ActivityLog;
